import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WizardFrame from "../components/WizardFrame.jsx";
import SupportFooter from "../components/SupportFooter.jsx";
import { importLandstarEligibilitySpreadsheet } from "../services/mockRedemptionApi.js";
import { validateLandstarEligibilitySpreadsheetFile } from "../services/eligibilitySpreadsheetValidation.js";
import {
  LANDSTAR_IMPORT_SESSION_KEY,
  landstarImportSteps,
} from "../utils/constants.js";
import { normalizeLandstarEmail } from "../utils/helpers.js";
import "../styles/index.css";

function LandstarEligibilityImportPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileValidation, setFileValidation] = useState(null);
  const [validatingFile, setValidatingFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const importAccess = useMemo(() => {
    const routeEmail = normalizeLandstarEmail(location.state?.email || "");
    const routeDriverCode = String(location.state?.driverCode || "").trim();

    if (routeEmail && routeDriverCode) {
      return {
        email: routeEmail,
        driverCode: routeDriverCode,
      };
    }

    try {
      const storedAccess = JSON.parse(
        window.sessionStorage.getItem(LANDSTAR_IMPORT_SESSION_KEY) || "{}"
      );

      return {
        email: normalizeLandstarEmail(storedAccess.email || ""),
        driverCode: String(storedAccess.driverCode || "").trim(),
      };
    } catch {
      return {
        email: "",
        driverCode: "",
      };
    }
  }, [location.state]);

  const hasImportAccess = Boolean(importAccess.email && importAccess.driverCode);

  const handleFileChange = async (event) => {
    const nextFile = event.target.files?.[0] || null;
    setSelectedFile(nextFile);
    setFileValidation(null);
    setError("");
    setResult(null);

    if (!nextFile) {
      return;
    }

    try {
      setValidatingFile(true);
      const validation = await validateLandstarEligibilitySpreadsheetFile(nextFile);
      setFileValidation(validation);
    } catch (validationError) {
      setError(validationError.message || "Unable to validate the spreadsheet headers.");
    } finally {
      setValidatingFile(false);
    }
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setError("Choose an .xlsx or .xls file before uploading.");
      return;
    }

    if (!fileValidation) {
      setError("The spreadsheet must pass header validation before it can be uploaded.");
      return;
    }

    try {
      setUploading(true);
      setError("");
      const response = await importLandstarEligibilitySpreadsheet(selectedFile, importAccess);
      setResult(response);
    } catch (uploadError) {
      setError(uploadError.message || "Unable to import the eligibility spreadsheet.");
    } finally {
      setUploading(false);
    }
  };

  const handleExit = () => {
    window.sessionStorage.removeItem(LANDSTAR_IMPORT_SESSION_KEY);
    navigate("/landstar-safety-redemption");
  };

  if (!hasImportAccess) {
    return (
      <div className="landstar-page-shell">
        <div className="landstar-wizard-shell">
          <div className="landstar-wizard-card">
            <div className="landstar-wizard-stage">
              <div className="landstar-step-content">
                <div className="landstar-entry-card landstar-access-card">
                  <p className="landstar-kicker">Admin Access Required</p>
                  <h3>This import page is restricted</h3>
                  <p className="landstar-intro-copy">
                    Only allowlisted Landstar admin emails can upload eligibility spreadsheets.
                  </p>
                  <hr className="landstar-section-divider" />
                  <div className="landstar-inline-actions landstar-inline-actions-start">
                    <button
                      type="button"
                      className="landstar-primary-button"
                      onClick={handleExit}
                    >
                      Return to Landstar Access
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SupportFooter />
      </div>
    );
  }

  return (
    <div className="landstar-page-shell">
      <WizardFrame
        title="Landstar Eligibility Import"
        steps={landstarImportSteps}
        currentStep="import"
        maxStepReached={0}
        onStepSelect={() => {}}
      >
        <div className="landstar-step-content">
          <div className="landstar-entry-card landstar-import-card">
            <p className="landstar-kicker">Admin Import</p>
            <h3>Upload the eligibility spreadsheet</h3>
            <div className="landstar-status-card">
                Signed in as 
                <br></br>
                <br></br>
                <strong>{importAccess.email}</strong>
            </div>
            <br></br>
            <ol className="landstar-intro-list landstar-access-list">
              <li>
                <strong>Use the approved template columns.</strong>
              </li>
              <li>
                <strong>Upload one file at a time.</strong>
              </li>
              <li>
                <strong>Review skipped rows after submission.</strong>
              </li>
            </ol>
            <br></br>
            <form className="landstar-step-form landstar-step-form-compact" onSubmit={handleUpload}>
              <input
                id="landstar-eligibility-file"
                type="file"
                accept=".xlsx,.xls"
                disabled={uploading || validatingFile}
                onChange={handleFileChange}
              />

              {selectedFile ? (
                <p className="landstar-helper-text">
                  Selected file: <strong>{selectedFile.name}</strong>
                </p>
              ) : null}

              {validatingFile ? (
                <p className="landstar-helper-text">Checking spreadsheet headers...</p>
              ) : null}

              {fileValidation ? (
                <p className="landstar-helper-text" aria-live="polite">
                  Headers verified on <strong>{fileValidation.sheetName}</strong>. Found{" "}
                  <strong>{fileValidation.totalRows}</strong> data rows.
                </p>
              ) : null}

              {error ? <p className="landstar-error">{error}</p> : null}

              <hr className="landstar-section-divider" />

              <div className="landstar-inline-actions landstar-inline-actions-start">
                <button
                  type="submit"
                  className="landstar-primary-button"
                  disabled={uploading || validatingFile || !fileValidation}
                >
                  {uploading ? "Uploading Spreadsheet..." : "Upload and Import"}
                </button>
                <button
                  type="button"
                  className="landstar-secondary-button"
                  onClick={handleExit}
                  disabled={uploading}
                >
                  Exit Import
                </button>
              </div>

              {uploading ? (
                <div className="landstar-inline-progress" aria-live="polite">
                  <span className="landstar-inline-progress-bar" />
                  <p className="landstar-helper-text">
                    Sending the spreadsheet to the import Lambda and updating Dynamo now.
                  </p>
                </div>
              ) : null}
            </form>

            {result ? (
              <div className="landstar-status-card landstar-import-results">
                <p className="landstar-kicker">Import Summary</p>
                <div className="landstar-status-grid">
                  <p>
                    <strong>Imported:</strong> {result.importedCount}
                  </p>
                  <p>
                    <strong>Total Rows:</strong> {result.totalRows}
                  </p>
                  <p>
                    <strong>Skipped Rows:</strong> {result.skippedRows}
                  </p>
                </div>

                {Array.isArray(result.errors) && result.errors.length ? (
                  <>
                    <hr className="landstar-section-divider" />
                    <div className="landstar-success-grid">
                      {result.errors.map((message) => (
                        <p key={message}>{message}</p>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="landstar-helper-text">
                    No row-level validation issues were reported by the import Lambda.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </WizardFrame>
      <SupportFooter />
    </div>
  );
}

export default LandstarEligibilityImportPage;
